/**
 * The "congratulate on the entry" reminder (תזכורת לברך על הכניסה לנכס) offered when a sale
 * listing is marked sold (נמכר) — the sold dialog's bar above אישור. Pure, no I/O.
 *
 * The reminder is an ordinary all-day TASK, put in the calendar of one of the listing's
 * seller-side realtors (`ownersData`). One that is not the signed-in user goes through the
 * office-task route, as any task assigned to a colleague (CalendarContext); see webot-server
 * `docs/rules/calendar-task-assignment.md`.
 *
 * Days travel as `YYYY-MM-DD` strings on the Israel calendar day, like the form's own date inputs
 * and `lease.ts`.
 */

import { getTaskStartEndDate } from "utils/calendar/helper";
import { normalizeUserEmail } from "utils/calendar/officeTasks";
import { addDays, daysBetween } from "./lease";
import type { AdditionalUser } from "../../@types/listings/user";
import type { CalendarEvent, CalendarEventAttendee, ProcessedListingData } from "../../@types/sharedSchema";

/** When the reminder falls. `entryDay` exists only when the listing has a recorded entry date. */
export type EntryCongratsDue = "entryDay" | "nextWeek" | "nextMonth" | "custom";

export interface EntryCongratsDraft {
  enabled: boolean;
  /** The realtor picked in the bar. null = not picked: the default applies. */
  assigneeEmail: string | null;
  /** null = not picked: the entry day when recorded, else in a month. */
  due: EntryCongratsDue | null;
  customDay: string | null;
}

export const INITIAL_ENTRY_CONGRATS: EntryCongratsDraft = {
  enabled: true,
  assigneeEmail: null,
  due: null,
  customDay: null
};

export interface EntryCongratsCandidate {
  email: string;
  name: string;
  avatarUrl?: string;
  isMe: boolean;
}

export interface EntryCongratsPlan {
  assigneeEmail: string;
  due: EntryCongratsDue;
  options: EntryCongratsDue[];
  /** The task's day; null only for a custom day not chosen yet (or in the past). */
  day: string | null;
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// CalendarForm's default event colour, the one every quick-added task carries.
const DEFAULT_TASK_COLOR = "#1890FF";

function isDay(value: unknown): value is string {
  return typeof value === "string" && DAY_PATTERN.test(value);
}

/** The same day `months` months later; a day the target month lacks (the 31st) becomes its last. */
export function addMonthsToDay(day: string, months: number): string {
  const [year, month, date] = day.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDate = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(date, lastDate));
  return target.toISOString().slice(0, 10);
}

/**
 * Who may get the reminder: the seller-side realtors who are the user or an office colleague, in
 * the order the dialog lists them. A realtor outside the office (חיצוני) has no Webot calendar the
 * user may write to — the server would refuse it as `not_a_colleague`.
 */
export function entryCongratsCandidates(
  sellerSide: AdditionalUser[],
  officeUsers: AdditionalUser[],
  me: string
): EntryCongratsCandidate[] {
  const self = normalizeUserEmail(me);
  const office = new Map(officeUsers.map((user) => [normalizeUserEmail(user.email), user]));
  const candidates: EntryCongratsCandidate[] = [];

  sellerSide.forEach((owner) => {
    const email = normalizeUserEmail(owner.email);
    if (!email || candidates.some((candidate) => candidate.email === email)) return;
    if (email !== self && !office.has(email)) return;

    const officeUser = office.get(email);
    candidates.push({
      email,
      name: owner.name || officeUser?.name || email,
      avatarUrl: owner.avatarUrl || officeUser?.avatarUrl,
      isMe: email === self
    });
  });

  return candidates;
}

/**
 * The listing's entry day (תאריך כניסה, stored as `evictionDate`), when it is a real date still
 * ahead. The form keeps a date only for a "specific date" occupancy; with "immediate" or
 * "flexible" a stale value can linger in the field, so it is ignored.
 */
export function recordedEntryDay(
  evictionType: string | null | undefined,
  evictionDate: unknown,
  todayDay: string
): string | null {
  const isSpecificDate = !evictionType || evictionType === "specificDate";
  if (!isSpecificDate || !isDay(evictionDate)) return null;
  return daysBetween(todayDay, evictionDate) >= 0 ? evictionDate : null;
}

/** What the bar stands for right now: the draft with every unpicked or stale choice defaulted. */
export function resolveEntryCongrats(
  draft: EntryCongratsDraft,
  {
    candidates,
    me,
    entryDay,
    todayDay
  }: { candidates: EntryCongratsCandidate[]; me: string; entryDay: string | null; todayDay: string }
): EntryCongratsPlan {
  const self = normalizeUserEmail(me);
  const picked = normalizeUserEmail(draft.assigneeEmail);
  const assigneeEmail = candidates.some((candidate) => candidate.email === picked)
    ? picked
    : candidates.find((candidate) => candidate.isMe)?.email ?? candidates[0]?.email ?? self;

  const options: EntryCongratsDue[] = entryDay
    ? ["entryDay", "nextWeek", "nextMonth", "custom"]
    : ["nextWeek", "nextMonth", "custom"];
  const due = draft.due && options.includes(draft.due) ? draft.due : entryDay ? "entryDay" : "nextMonth";

  return { assigneeEmail, due, options, day: dayFor(due, { entryDay, customDay: draft.customDay, todayDay }) };
}

function dayFor(
  due: EntryCongratsDue,
  { entryDay, customDay, todayDay }: { entryDay: string | null; customDay: string | null; todayDay: string }
): string | null {
  switch (due) {
    case "entryDay":
      return entryDay;
    case "nextWeek":
      return addDays(todayDay, 7);
    case "nextMonth":
      return addMonthsToDay(todayDay, 1);
    case "custom":
      return isDay(customDay) && daysBetween(todayDay, customDay) >= 0 ? customDay : null;
  }
}

/** The seller as the task's attendee — only with a phone, which is what links a task to a contact. */
export function sellerAttendee(seller: {
  firstName?: string;
  lastName?: string;
  phoneInternational?: string | null;
}): CalendarEventAttendee | null {
  if (!seller.phoneInternational) return null;
  return {
    firstName: seller.firstName?.trim() ?? "",
    lastName: seller.lastName?.trim() ?? "",
    phoneNumberInternational: seller.phoneInternational
  };
}

/**
 * The reminder task: all-day on `day`, linked to the listing (so it shows in the property file)
 * and to the seller. Tasks never message the people on them, so the seller is not contacted.
 */
export function buildEntryCongratsTask({
  title,
  day,
  attendee,
  listingData
}: {
  title: string;
  day: string;
  attendee: CalendarEventAttendee | null;
  listingData: ProcessedListingData;
}): CalendarEvent {
  const [year, month, date] = day.split("-").map(Number);
  const { start, end } = getTaskStartEndDate(new Date(year, month - 1, date));

  return {
    title,
    description: "",
    textColor: DEFAULT_TASK_COLOR,
    backgroundColor: DEFAULT_TASK_COLOR,
    allDay: true,
    start,
    end,
    eventType: "task",
    status: "pending",
    attendees: attendee ? [attendee] : [],
    listingIds: [listingData.id],
    listingsData: [listingData]
  };
}
